import { useEffect, useState } from 'react';

/**
 * 2026-09-15 (właściciel): nowa wersja panelu ma być widoczna bez twardego reloadu.
 * Hook porównuje nazwę bundla `main.<hash>.js` z aktualnie załadowanym skryptem z tą w świeżo
 * pobranym `/index.html` (no-store) — przy każdym powrocie do karty i co 5 minut. Różnica = deploy
 * → `true`, a baner pyta o odświeżenie (nie przeładowujemy sami: użytkownik może być w środku edycji).
 */
const MAIN_RE = /main\.[a-z0-9]+\.js/;

export function currentMainBundle(doc: Document = document): string | null {
  const scripts = Array.from(doc.querySelectorAll('script[src]')) as HTMLScriptElement[];
  for (const s of scripts) {
    const m = MAIN_RE.exec(s.src || '');
    if (m) return m[0];
  }
  return null;
}

export function bundleFromHtml(html: string): string | null {
  const m = MAIN_RE.exec(html || '');
  return m ? m[0] : null;
}

export async function checkNewVersion(fetchImpl: typeof fetch = fetch, doc: Document = document): Promise<boolean> {
  const current = currentMainBundle(doc);
  if (!current) return false;
  try {
    const res = await fetchImpl(`/index.html?v=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return false;
    const fresh = bundleFromHtml(await res.text());
    return !!fresh && fresh !== current;
  } catch {
    return false; // offline / błąd sieci = nie ma czego ogłaszać
  }
}

export function useNewVersion(intervalMs = 5 * 60 * 1000): boolean {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    let alive = true;
    const run = async () => { if (!available && (await checkNewVersion())) { if (alive) setAvailable(true); } };
    const onVisible = () => { if (document.visibilityState === 'visible') run(); };
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(run, intervalMs);
    return () => { alive = false; document.removeEventListener('visibilitychange', onVisible); window.clearInterval(timer); };
  }, [available, intervalMs]);
  return available;
}
